import type { QuoteDimension } from "@workspace/api-zod/quantity-dimensions";

export type DimensionRow = {
  label: string;
  dimensions: QuoteDimension[];
  displayUnit: string;
};

/** Presentation only: group explicit axes of the same named object, never infer geometry. */
export function dimensionRows(dimensions: QuoteDimension[], separateAxes = false): DimensionRow[] {
  const candidates = new Map<string, { label: string; axes: string[]; dimensions: QuoteDimension[] }>();
  for (const d of dimensions) {
    const match = d.unit === "m" && d.label.match(/^(.+?)\s+(width|height|length|depth|thickness|diameter)$/i);
    if (!match || separateAxes) continue;
    const key = match[1].toLowerCase();
    const candidate = candidates.get(key) ?? { label: match[1], axes: [], dimensions: [] };
    candidate.axes.push(match[2].toLowerCase());
    candidate.dimensions.push(d);
    candidates.set(key, candidate);
  }
  const groups = new Map<string, QuoteDimension[]>();
  const labels = new Map<string, string>();
  for (const c of candidates.values()) {
    // Repeated axes are ambiguous; leave those measurements individually labelled.
    if (c.dimensions.length < 2 || new Set(c.axes).size !== c.axes.length) continue;
    // Each row should have a single provenance status, never mixed badges.
    if (new Set(c.dimensions.map(d => d.source)).size !== 1) continue;
    for (const d of c.dimensions) {
      groups.set(d.id, c.dimensions);
      labels.set(d.id, c.label);
    }
  }
  const used = new Set<string>();
  const rows: DimensionRow[] = [];
  for (const d of dimensions) {
    if (used.has(d.id)) continue;
    const members = groups.get(d.id) ?? [d];
    members.forEach(member => used.add(member.id));
    // Small linear components read naturally in millimetres; larger lengths stay in metres.
    const displayUnit = d.unit === "m" && members.some(member => member.value > 0 && member.value < 1)
      ? "mm" : d.unit;
    rows.push({ label: labels.get(d.id) ?? d.label, dimensions: members, displayUnit });
  }
  return rows;
}

export function dimensionDisplayValue(value: number, storedUnit: string, displayUnit: string): number {
  return storedUnit === "m" && displayUnit === "mm" ? value * 1000 : value;
}

export function dimensionStoredValue(value: number, storedUnit: string, displayUnit: string): number {
  return storedUnit === "m" && displayUnit === "mm" ? value / 1000 : value;
}

export function formatDimension(value: number): string {
  return new Intl.NumberFormat("en-GB", { useGrouping: false, maximumFractionDigits: 6 }).format(value);
}
